import React, { useState, useRef, useEffect } from "react";
import { Bell, ChevronDown } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import styles from "./Header.module.css";
import axios from "axios";
import { BASE_URL } from '../../Pages/Api/Api';
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const routeNames = {
  "/dashboard": "Dashboard",
  "/Reconcilation": "Reconciliation Runs",
  "/Reconcilation/Workspace": "Workspace",
  "/Reconcilation/Exceptions": "Exceptions",
  "/Reconcilation/MatchingRules": "Matching Rules",
  "/bank-transactions": "Bank Transactions",
  "/bank-transactions/ledger": "Ledger Transactions",
  "/bank-transactions/matched": "Matched Transactions",
  "/bank-transactions/unmatched": "Unmatched Transactions",
  "/data/import-centre": "Import Centre",
  "/data/bank-accounts": "Bank Accounts",
  "/data/data-sources": "Data Sources",
  "/approvals": "Approvals",
  "/reports/reconciliation": "Reconciliation Reports",
  "/reports/exceptions": "Exception Reports",
  "/reports/ageing": "Aging Reports",
  "/reports/management": "Management Reports",
  "/audit-trail": "Audit Trail",
  "/administration/users": "Users",
  "/administration/roles-permissions": "Roles & Permissions",
  "/administration/organization": "Organization",
  "/administration/integrations": "Integrations",
  "/administration/notifications": "Notifications",
  "/administration/security": "Security",
};

const notificationData = [
  {
    id: 1,
    title: "New reconciliation run completed",
    description: "Your daily bank reconciliation is ready for review.",
    time: "2 mins ago",
    type: "success",
  },
  {
    id: 2,
    title: "Exception requires attention",
    description: "3 unmatched transactions were flagged in the GTBank import.",
    time: "26 mins ago",
    type: "warning",
  },
  {
    id: 3,
    title: "Approval requested",
    description: "A management approval is waiting on the monthly close report.",
    time: "1 hour ago",
    type: "info",
  },
  {
    id: 4,
    title: "System maintenance",
    description: "Scheduled maintenance will begin tonight at 11:00 PM WAT.",
    time: "Yesterday",
    type: "neutral",
  },
];

const toTitleCase = (value) =>
  value
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());



const Header = () => {
    const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const profileRef = useRef(null);
  const notificationRef = useRef(null);
  const navigation = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pathname = location.pathname;
  const matchedRoute = routeNames[pathname] ||
    Object.keys(routeNames)
      .filter((route) => pathname.startsWith(route) && route !== "/")
      .sort((a, b) => b.length - a.length)[0];

  const currentRouteLabel =
    routeNames[pathname] ||
    (matchedRoute ? routeNames[matchedRoute] : toTitleCase(pathname.split("/").filter(Boolean).pop() || "Dashboard"));

  const breadcrumbPath = ["/dashboard"];

  if (pathname !== "/dashboard") {
    const pathSegments = pathname.split("/").filter(Boolean);
    let builtPath = "";

    pathSegments.forEach((segment) => {
      builtPath += `/${segment}`;
      const routeKey = builtPath === "/" ? "/dashboard" : builtPath;
      if (routeNames[routeKey]) {
        breadcrumbPath.push(routeKey);
      }
    });
  }

  const breadcrumbs = breadcrumbPath.map((route) => ({
    label: route === "/dashboard" ? "Dashboard" : routeNames[route] || toTitleCase(route.split("/").pop()),
    path: route,
  }));

    // const handleLogout = () => {
    //     console.log("Logout clicked");
    //     setOpen(false);
    // };

      // ----- Logout (existing) -----
const handleLogout = () => {
    setOpen(false); 

    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of your session.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "rgb(222, 36, 54)",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem("userToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        try {
          await axios.post(`${BASE_URL}/logout`, {}, { headers });
        } catch (err) {
          console.error("Logout request failed", err);
        }

        // Clear local storage data
        localStorage.removeItem("userToken");
        localStorage.removeItem("firstName");
        localStorage.removeItem("lastName");
        localStorage.removeItem("phone");
        localStorage.removeItem("email");
        localStorage.removeItem("userType");
        localStorage.removeItem("customerId");

        // Redirect to login page
        navigate("/login");
      }
    });
  };


  return (
    <>
      <header className={styles.header}>
        <div className={styles.leftRow}>
          <div className={styles.routeMeta}>
            <h2 className={styles.pageTitle}>{currentRouteLabel}</h2>
            <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={`${crumb.path}-${index}`}>
                  {index > 0 && <span className={styles.separator}>/</span>}
                  <Link to={crumb.path} className={styles.breadcrumbLink}>
                    {crumb.label}
                  </Link>
                </React.Fragment>
              ))}
            </nav>
          </div>
        </div>

        <div className={styles.rightRow}>
          <div className={styles.notificationWrap} ref={notificationRef}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label="Notifications"
              onClick={() => setNotificationsOpen((prev) => !prev)}
            >
              <Bell size={18} />
              <span className={styles.notificationBadge}>3</span>
            </button>
          </div>

          <div className={styles.profileContainer} ref={profileRef}>
            <div className={styles.avatar}>AO</div>

            <div className={styles.profile}>
              <button
                type="button"
                className={styles.profileBtn}
                onClick={() => setProfileOpen((v) => !v)}
                aria-expanded={profileOpen}
              >
                <div className={styles.profileText}>
                  <div className={styles.name}>Adekunle O.</div>
                  <div className={styles.role}>Super Admin</div>
                </div>
                <ChevronDown className={styles.chev} />
              </button>

              <div className={`${styles.dropdown} ${profileOpen ? styles.open : ""}`} role="menu">
                <button type="button" className={styles.dropdownItem} role="menuitem" onClick={() => navigation('/my-profile')}>Profile</button>
                {/* <button type="button" className={styles.dropdownItem} role="menuitem">Settings</button> */}
                <div className={styles.dropdownDivider} />
                <button type="button" className={styles.dropdownItem} role="menuitem" onClick={handleLogout}>Logout</button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <aside
        className={`${styles.notificationDrawer} ${notificationsOpen ? styles.drawerOpen : ""}`}
        aria-label="Notifications panel"
      >
        <div className={styles.drawerHeader}>
          <div>
            <p className={styles.drawerEyebrow}>Updates</p>
            <h3>Notifications</h3>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => setNotificationsOpen(false)}
            aria-label="Close notifications"
          >
            ×
          </button>
        </div>

        <div className={styles.drawerList}>
          {notificationData.map((item) => (
            <div key={item.id} className={styles.notificationItem}>
              <span className={`${styles.dot} ${styles[item.type]}`} />
              <div className={styles.notificationText}>
                <div className={styles.notificationTopRow}>
                  <strong>{item.title}</strong>
                  <span>{item.time}</span>
                </div>
                <p>{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default Header;